import React from 'react';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';
import classNames from 'styles/utils/classNames';
import { TickerCell } from '../components/TickerCell';
import { TickerType } from '../types';
import { formatMarketCap, CustomColumnDef } from 'screens/Calendar';

/**
 * Orders two calendar days (`YYYY-MM-DD`). Text in that form sorts by date, so no Date is built
 * and no time zone can move a day. Missing days ('--' or empty) sort last.
 */
export function compareCalendarDays(a?: string, b?: string): number {
  const hasA = !!a && a !== '--';
  const hasB = !!b && b !== '--';

  if (!hasA || !hasB) return Number(!hasA) - Number(!hasB);

  return a.localeCompare(b);
}

export function annualizeYield(yieldValue?: number, frequency?: string): string {
  if (!yieldValue || isNaN(yieldValue)) return '--';

  const freqMap: Record<string, number> = {
    Annual: 1,
    'Semi-Annual': 2,
    Quarterly: 4,
    Monthly: 12,
  };

  const multiplier = freqMap[frequency ?? ''] || freqMap['Annual'];
  const result = yieldValue * multiplier;

  return `${result.toFixed(2)}%`;
}

export const createDividendsColumns = (
  bookmarkedItems: Set<string>,
  handleBookmark: (symbol: string) => void,
  tickerData: TickerType[] = [],
  slug?: string,
): CustomColumnDef<any>[] => [
    {
      accessorKey: 'symbol',
      header: 'Ticker',
      cell: ({ row }) => {
        const ticker = row.original.symbol;
        const isBookmarked = bookmarkedItems.has(ticker);

        return (
          <div className="group/row relative">
            <div className="group relative ml-2">
              <button
                onClick={() => handleBookmark(ticker)}
                className={classNames(
                  'absolute -left-[20px] top-1/2 z-10 -translate-y-1/2 p-1 transition-all duration-200',
                  'group-hover/row:opacity-100',
                  isBookmarked
                    ? 'text-yellow-400 opacity-100'
                    : 'text-gray-400 opacity-70 hover:text-yellow-400 hover:opacity-100',
                )}
              >
                {isBookmarked ? (
                  <AiFillStar className="h-3 w-3" />
                ) : (
                  <>
                    <AiOutlineStar className="h-3 w-3 group-hover:hidden" />
                    <AiFillStar className="hidden h-3 w-3 group-hover:block" />
                  </>
                )}
              </button>
              <TickerCell ticker={ticker} tickerData={tickerData} slug={slug} />
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'marketCap',
      header: 'Market Cap',
      cell: ({ row }) => formatMarketCap(row.original.marketCap),
      csvType: 'currency-large',
    },
    {
      accessorKey: 'declarationDate',
      header: 'Declaration Date',
      cell: ({ row }) => {
        const date = row.original.declarationDate;
        return date && date !== '--' ? date : '--';
      },
      csvType: 'date',
    },
    {
      id: 'exDividendDate',
      header: 'Ex-Dividend Date',
      // The provider's ex-dividend date, a plain day (see DividendsCalendarPage).
      accessorFn: (row) => row.date,
      cell: ({ row }) => row.original.date || '--',
      csvType: 'date',
      sortingFn: (rowA, rowB) => compareCalendarDays(rowA.original.date, rowB.original.date),
    },
    {
      accessorKey: 'recordDate',
      header: 'Record Date',
      cell: ({ row }) => row.original.recordDate || '--',
      csvType: 'date',
    },
    {
      accessorKey: 'paymentDate',
      header: 'Payment Date',
      cell: ({ row }) => row.original.paymentDate || '--',
      csvType: 'date',
    },
    {
      accessorKey: 'adjDividend',
      header: 'Adj. Dividend',
      csvType: 'currency',
      cell: ({ row }) => {
        const value = row.original.adjDividend;
        return typeof value === 'number'
          ? value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 4,
          })
          : '--';
      },
    },
    {
      accessorKey: 'dividend',
      header: 'Dividend',
      csvType: 'currency',
      cell: ({ row }) => {
        const value = row.original.dividend;
        return typeof value === 'number'
          ? value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 4,
          })
          : '--';
      },
    },
    {
      accessorKey: 'yield',
      header: 'Yield',
      csvType: 'percentage',
      cell: ({ row }) => {
        const yieldValue = row.original?.yield;
        return typeof yieldValue === 'number' && !isNaN(yieldValue)
          ? `${yieldValue.toFixed(2)}%`
          : '--';
      },
    },
    {
      header: 'Annual Yield',
      accessorKey: 'annualYield',
      csvType: 'percentage',
      cell: ({ row }) => annualizeYield(row.original.yield, row.original.frequency),
      accessorFn: (row) => {
        const yieldValue = row.yield;
        const frequency = row.frequency;

        if (!yieldValue || isNaN(yieldValue)) return -1;

        const freqMap: Record<string, number> = {
          Annual: 1,
          'Semi-Annual': 2,
          Quarterly: 4,
          Monthly: 12,
        };

        const multiplier = freqMap[frequency ?? ''] || freqMap['Annual'];
        return yieldValue * multiplier;
      },
      sortingFn: (rowA, rowB) => {
        const valueA = rowA.getValue('annualYield') as number;
        const valueB = rowB.getValue('annualYield') as number;

        if (valueA === -1 && valueB === -1) return 0;
        if (valueA === -1) return 1;
        if (valueB === -1) return -1;

        return valueA - valueB;
      },
    },
    {
      accessorKey: 'frequency',
      header: 'Frequency',
      cell: ({ row }) => row.original.frequency || '--',
    },
  ];
